import React, { useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { formatDuration } from '../../utils/studyTracking';
import { displayExamScores } from '../../utils/questionBank';

function Field({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <div style={st.field}>
      <span style={st.label}>{label}</span>
      <span style={st.value}>{value}</span>
    </div>
  );
}

export default function StudentFullProfileModal({ subscription, onClose }) {
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [exams, setExams] = useState(null);
  const [quizzes, setQuizzes] = useState([]);

  const userId = subscription?.userId;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!userId) {
        setLoading(false);
        return;
      }
      try {
        const [progSnap, trackSnap, examSnap, quizSnap] = await Promise.all([
          getDoc(doc(db, 'userProgress', userId)),
          getDoc(doc(db, 'studyTracking', userId)),
          getDoc(doc(db, 'examAttempts', userId)),
          getDocs(collection(db, 'userQuizzes', userId, 'items')),
        ]);
        if (cancelled) return;
        setProgress(progSnap.exists() ? progSnap.data() : null);
        setTracking(trackSnap.exists() ? trackSnap.data() : null);
        setExams(examSnap.exists() ? examSnap.data() : null);
        setQuizzes(quizSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.warn(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const completed = Object.values(progress?.completedLessons || {}).filter(Boolean).length;
  const lecturePassed = quizzes.filter((q) => q.kind === 'lecture' && q.passed).length;
  const modulePassed = quizzes.filter((q) => q.kind === 'module' && q.passed).length;
  const bestScores = displayExamScores(exams);

  return (
    <div style={st.overlay} onClick={onClose} role="presentation">
      <div style={st.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Student full profile">
        <div style={st.head}>
          <div>
            <p style={st.kicker}>Student profile</p>
            <h2 style={st.title}>{subscription.planLabel || 'Subscription'}</h2>
          </div>
          <button type="button" onClick={onClose} style={st.close} aria-label="Close">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {loading ? (
          <p style={st.loading}>Loading full details…</p>
        ) : (
          <div style={st.body}>
            <section style={st.card}>
              <h3 style={st.section}>Account & enrollment</h3>
              <div style={st.grid}>
                <Field label="Plan" value={subscription.planLabel} />
                <Field label="Status" value={subscription.status} />
              </div>
            </section>

            <section style={st.card}>
              <h3 style={st.section}>Learning record</h3>
              <div style={st.grid}>
                <Field label="Lectures completed" value={String(completed)} />
                <Field label="Lecture quizzes passed" value={String(lecturePassed)} />
                <Field label="Module quizzes passed" value={String(modulePassed)} />
                <Field label="Study time" value={formatDuration(tracking?.totalActiveSeconds || 0)} />
                <Field label="Portal days" value={String(tracking?.loginDays || 0)} />
              </div>
              {Object.keys(bestScores).length > 0 ? (
                <div style={{ marginTop: 12 }}>
                  {Object.entries(bestScores).map(([sub, score]) => (
                    <p key={sub} style={st.examLine}>
                      {score?.examTitle || score?.subject || sub}: {score?.percentage != null ? `${score.percentage}%` : '—'}
                    </p>
                  ))}
                </div>
              ) : (
                <p style={st.muted}>No practice exams taken yet.</p>
              )}
            </section>

            <section style={st.card}>
              <h3 style={st.section}>Recent quizzes</h3>
              {quizzes.length === 0 ? (
                <p style={st.muted}>No lecture or module quizzes yet.</p>
              ) : (
                quizzes
                  .slice()
                  .sort((a, b) => (b.lastPct || 0) - (a.lastPct || 0))
                  .slice(0, 12)
                  .map((q) => (
                    <div key={q.id} style={st.quizRow}>
                      <strong>{q.title || q.id}</strong>
                      <span>
                        {q.kind} · {q.lastPct != null ? `${q.lastPct}%` : '—'} · {q.attempts || 0} try
                        {(q.attempts || 0) === 1 ? '' : 'ies'}
                        {q.passed ? ' · passed' : q.cleared ? ' · continued' : ''}
                      </span>
                    </div>
                  ))
              )}
            </section>

          </div>
        )}
      </div>
    </div>
  );
}

const st = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.55)',
    zIndex: 80,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  modal: {
    width: 'min(760px, 100%)',
    maxHeight: '90vh',
    overflow: 'auto',
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 24px 60px rgba(15,23,42,0.25)',
  },
  head: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    padding: '18px 20px',
    borderBottom: '1px solid #e2e8f0',
  },
  kicker: { margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#64748b' },
  title: { margin: '4px 0 2px', fontSize: 20, fontWeight: 800, color: '#1b365d' },
  sub: { margin: 0, fontSize: 13, color: '#64748b' },
  close: {
    border: 'none',
    background: '#f1f5f9',
    width: 36,
    height: 36,
    borderRadius: 10,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loading: { padding: 32, textAlign: 'center', color: '#64748b' },
  body: { padding: 16, display: 'grid', gap: 12 },
  card: { border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, background: '#f8fafc' },
  section: { margin: '0 0 10px', fontSize: 13, fontWeight: 800, color: '#1b365d' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8 },
  field: { display: 'grid', gap: 2 },
  label: { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#94a3b8' },
  value: { fontSize: 13, color: '#334155', wordBreak: 'break-word' },
  muted: { margin: 0, fontSize: 13, color: '#94a3b8' },
  examLine: { margin: '0 0 4px', fontSize: 13, color: '#334155' },
  quizRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: '8px 0',
    borderBottom: '1px solid #e2e8f0',
    fontSize: 12,
    color: '#64748b',
  },
};
